import { LEVEL_COUNTS } from '../lib/schoolDirectory';
import React from 'react';
import {
  RefreshCw, GraduationCap, BookOpen, Phone, Globe,
  ExternalLink, Award, Heart, Target, Briefcase, DollarSign
} from 'lucide-react';
import { BackButton, ExpandableCard, FAQAccordion, ResourceLinkGrid, ShowMoreList, DirectoryLink } from './shared';

// ==================== MOTIVATION SECTION ====================
const MotivationSection = () => {
  const stories = [
    {
      quote: "I dropped out in Grade 10 to help my family. At 25, I went to HEART and got my certification. Now I run my own business.",
      name: "Marcus T.",
      path: "HEART Graduate → Entrepreneur"
    },
    {
      quote: "Being a teen mom was hard, but the Women's Centre helped me finish my education. I'm now in university.",
      name: "Shanice M.",
      path: "Women's Centre → University Student"
    },
    {
      quote: "I thought it was too late at 35. I did CSEC evening classes while working. Now I'm pursuing my degree part-time.",
      name: "George W.",
      path: "Private Candidate → UTech Student"
    }
  ];

  return (
    <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl p-6 mb-8">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
          <Heart className="w-6 h-6 text-amber-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-amber-800">You Can Do This</h2>
          <p className="text-sm text-amber-600">It's never too late to continue your education</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        {stories.map((story, idx) => (
          <div key={idx} className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-gray-600 text-sm italic">"{story.quote}"</p>
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="font-semibold text-gray-800 text-sm">{story.name}</p>
              <p className="text-xs text-amber-600">{story.path}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const SECOND_CHANCE_FAQS = [
  {
    q: "I dropped out years ago. Can I still get my CSEC?",
    a: "Yes! You can sit CSEC exams as a private candidate at any age. Many people in their 20s, 30s, and beyond do this. Contact CXC or find an evening class program that registers private candidates."
  },
  {
    q: "I have no qualifications. Where do I start?",
    a: "HEART/NSTA Trust accepts students with no CSEC passes for many programmes. Start with a HEART assessment - they'll help you find the right starting point. Many programmes are free and include stipends."
  },
  {
    q: "I'm a teen mom. What options do I have?",
    a: "The Women's Centre of Jamaica Foundation specializes in helping young mothers continue their education. They offer childcare support, counseling, and help with school re-entry or CSEC preparation."
  },
  {
    q: "Can I study part-time while working?",
    a: "Absolutely! Most community colleges and UCC offer evening and weekend programmes. UWI Open Campus has online options. Many employers also support workers who want to study."
  },
  {
    q: "I can't afford tuition. What help is available?",
    a: "HEART programmes are mostly free. For college/university, check the Students' Loan Bureau (SLB), JAMVAT (veteran assistance), PATH tertiary grant, and institutional scholarships. Many schools have payment plans too."
  },
  {
    q: "I have a learning difference. Can I still succeed?",
    a: "Yes! You can apply for exam accommodations through CXC. Many institutions have support services for students with learning differences. Don't let this stop you - many successful people have learning differences."
  }
];

// ==================== MAIN SECOND CHANCE EDUCATION HUB ====================
export const SecondChanceEducationHub = ({ setPage, goBack }) => {
  const pathways = [
    {
      icon: Briefcase,
      title: "HEART/NSTA Trust",
      meta: "Free vocational training - no qualifications needed",
      description: "Jamaica's national training agency offers free or subsidized training in dozens of fields. Most programmes don't require CSEC passes to start.",
      tags: ["Free Training", "No CSEC Required", "Stipends Available", "Job Placement"],
      iconChipClassName: "bg-brand-500 text-white",
      requirements: "Jamaican citizen, 17+. No academic requirements for many programmes. Assessment determines starting level.",
      duration: "3 months to 2 years depending on programme and level",
      cost: "FREE for most programmes. Many include a monthly stipend.",
      steps: [
        "Visit any HEART centre or call for information",
        "Complete assessment to determine your level",
        "Choose from available programmes at your level",
        "Attend training (may include on-the-job experience)",
        "Earn NVQ-J certification recognized by employers"
      ],
      contact: {
        phone: "888-HEART-JA (888-432-7852)",
        website: "https://www.heart-nsta.org"
      }
    },
    {
      icon: BookOpen,
      title: "CSEC Private Candidate",
      meta: "Sit exams at your own pace, at any age",
      description: "You can sit CSEC exams without being enrolled in a school. Study at your own pace and register as a private candidate.",
      tags: ["Any Age", "Self-Paced", "Evening Classes", "Flexible"],
      iconChipClassName: "bg-blue-500 text-white",
      requirements: "No formal requirements. Must register through an accredited centre (not directly with CXC).",
      duration: "Depends on your pace. Many sit 1-3 subjects per year.",
      cost: "Approx. JMD $8,000-15,000 per subject (registration + materials). Evening classes additional.",
      steps: [
        "Decide which subjects you want to sit",
        "Find an evening class programme or study independently",
        "Find a school or centre that registers private candidates",
        "Register before the deadline (usually October for May/June exams)",
        "Prepare using past papers and study guides",
        "Sit your exams at the assigned centre"
      ],
      contact: {
        phone: "876-630-5200 (CXC Jamaica)",
        website: "https://www.cxc.org"
      }
    },
    {
      icon: Heart,
      title: "Women's Centre of Jamaica Foundation",
      meta: "Supporting teen mothers to continue education",
      description: "Specialized support for young women who became pregnant during school. Helps with education continuation, parenting skills, and career development.",
      tags: ["Teen Mothers", "Childcare Support", "Counseling", "Education"],
      iconChipClassName: "bg-pink-500 text-white",
      requirements: "Young women who became pregnant while in school.",
      duration: "Flexible - support continues as needed",
      cost: "Services are FREE",
      steps: [
        "Contact the Women's Centre or visit any parish office",
        "Complete intake and assessment",
        "Develop an education plan with counselors",
        "Access childcare support while studying",
        "Prepare for CSEC or re-entry to school"
      ],
      contact: {
        phone: "876-929-7608",
        website: "https://www.wcjf.org.jm"
      }
    },
    {
      icon: GraduationCap,
      title: "Mature Student Entry",
      meta: "University without traditional qualifications",
      description: "Most universities accept mature students (usually 21-25+) without traditional CAPE qualifications if you have work experience or alternative credentials.",
      tags: ["21+ Entry", "Work Experience", "Alternative Paths", "Degree Programmes"],
      iconChipClassName: "bg-door-tertiary text-white",
      requirements: "Usually 21-25+ years old. Work experience or demonstrated ability. May require foundation courses.",
      duration: "3-4 years for bachelor's degree (longer part-time)",
      cost: "Varies by institution. JMD $200,000-900,000/year. SLB loans available.",
      steps: [
        "Research mature entry requirements at your preferred university",
        "Gather documents showing work experience and any qualifications",
        "Apply for mature entry (may have separate application track)",
        "Complete any required foundation or bridging courses",
        "Apply for SLB loan if needed"
      ],
      contact: {
        website: "Contact individual universities for their mature entry policies"
      }
    },
    {
      icon: BookOpen,
      title: "Community College Pathways",
      meta: "Start with associates, transfer to university",
      description: "Community colleges offer associate degrees and certificates with flexible schedules. Many programmes accept students with fewer CSECs than university.",
      tags: ["Lower Entry Requirements", "Evening Classes", "Associate Degrees", "Transfer Options"],
      iconChipClassName: "bg-brand-500 text-white",
      requirements: "Typically 3-5 CSEC passes (fewer than university). Varies by programme.",
      duration: "2 years for associate degree",
      cost: "JMD $100,000-300,000/year. More affordable than university.",
      steps: [
        "Visit your nearest community college",
        "Inquire about programmes matching your interests",
        "Apply for admission (often rolling admissions)",
        "Look into SLB loans or payment plans",
        "Consider evening/weekend options if working"
      ],
      contact: {
        website: "See our Tertiary Education page for community college listings"
      }
    },
    {
      icon: Globe,
      title: "UWI Open Campus",
      meta: "Study online at your own pace",
      description: "The University of the West Indies offers online degree programmes. Study from anywhere in Jamaica while working or caring for family.",
      tags: ["Online Learning", "Flexible Schedule", "UWI Degree", "Work & Study"],
      iconChipClassName: "bg-indigo-500 text-white",
      requirements: "5 CSEC passes including English for most programmes. Mature entry available for 23+.",
      duration: "3-4 years (can go slower part-time)",
      cost: "Similar to on-campus UWI. SLB loans available.",
      steps: [
        "Visit open.uwi.edu to explore programmes",
        "Check your eligibility (or apply for mature entry)",
        "Apply online during admission periods",
        "Arrange financing (SLB available)",
        "Access your courses online and begin studying"
      ],
      contact: {
        phone: "876-702-4800",
        website: "https://open.uwi.edu"
      }
    }
  ];

  const relatedResources = [
    { title: 'Universities & Colleges', desc: 'Browse all institutions', icon: GraduationCap, page: 'tertiary' },
    { title: 'Career Explorer', desc: 'Match careers to training', icon: Briefcase, page: 'career-path' },
    { title: 'Scholarships', desc: 'Find funding opportunities', icon: Award, page: 'funding' }
  ];

  return (
    <div className="w-full">
      <BackButton goBack={goBack} />

      <DirectoryLink
        setPage={setPage}
        level="Tertiary"
        label="Search training & post-secondary institutions"
        count={LEVEL_COUNTS.Tertiary}
        className="mb-8"
      />

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-door-chance to-door-chance-dark rounded-2xl p-8 text-white mb-8">
        <div className="flex items-center gap-2 text-white/80 text-sm mb-2">
          <RefreshCw className="w-5 h-5" />
          <span>Second Chance Education</span>
        </div>
        <h1 className="text-3xl md:text-4xl font-black mb-3">
          Your Path Back to Education
        </h1>
        <p className="text-lg text-white/90 mb-6 max-w-2xl">
          It's never too late. Whether you dropped out, missed opportunities, or life took a different turn - there are real options waiting for you.
        </p>

        {/* Key Message */}
        <div className="bg-white/15 rounded-xl p-4 max-w-xl">
          <p className="text-white font-medium">
            No CSEC? No problem. Many programmes don't require any qualifications to start.
            You can build your way up, one step at a time.
          </p>
        </div>
      </div>

      {/* Motivation Section */}
      <MotivationSection />

      {/* Pathways */}
      <section className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Target className="w-6 h-6 text-amber-600" />
          Your Options
        </h2>
        {/* "Your Options" was a single 2,200px stack — the longest block on the
            page. Capped at the first four routes back into education; the rest
            stay one tap away. */}
        <ShowMoreList items={pathways} initial={4} noun="options" className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {(pathway, idx) => (
            <ExpandableCard key={idx} accent="chance" {...pathway}>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-800 text-sm mb-2">Requirements</h4>
                  <p className="text-sm text-gray-600">{pathway.requirements}</p>
                </div>

                {pathway.duration && (
                  <div>
                    <h4 className="font-semibold text-gray-800 text-sm mb-2">Duration</h4>
                    <p className="text-sm text-gray-600">{pathway.duration}</p>
                  </div>
                )}

                <div>
                  <h4 className="font-semibold text-gray-800 text-sm mb-2">Cost</h4>
                  <p className="text-sm text-gray-600">{pathway.cost}</p>
                </div>

                {pathway.steps && (
                  <div>
                    <h4 className="font-semibold text-gray-800 text-sm mb-2">How to Get Started</h4>
                    <ol className="space-y-2">
                      {pathway.steps.map((step, stepIdx) => (
                        <li key={stepIdx} className="flex items-start gap-2 text-sm text-gray-600">
                          <span className="bg-amber-100 text-amber-700 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {stepIdx + 1}
                          </span>
                          {step}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {pathway.contact && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <h4 className="font-semibold text-gray-800 text-sm mb-2">Contact</h4>
                    <div className="space-y-1 text-sm">
                      {pathway.contact.phone && (
                        <a href={`tel:${pathway.contact.phone}`} className="flex items-center gap-2 text-gray-600 hover:text-brand-600">
                          <Phone className="w-4 h-4" /> {pathway.contact.phone}
                        </a>
                      )}
                      {pathway.contact.website && (
                        <a href={pathway.contact.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-brand-600 hover:underline">
                          <Globe className="w-4 h-4" /> Visit Website <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </ExpandableCard>
          )}
        </ShowMoreList>
      </section>

      {/* Earn While You Learn */}
      <div className="mb-8">
        <h3 className="text-xl font-bold text-gray-800 mb-4">Earn While You Learn</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <button
            onClick={() => setPage('side-hustles')}
            className="bg-gradient-to-br from-brand-500 to-brand-600 rounded-xl p-6 text-left text-white shadow-md hover:shadow-lg transition-all"
          >
            <DollarSign className="w-10 h-10 mb-3 opacity-90" />
            <h4 className="font-bold text-lg">Student Side Hustles</h4>
            <p className="text-brand-100 text-sm mt-1">Safe, legal ways to earn while you learn</p>
          </button>
          <button
            onClick={() => setPage('summer-employment')}
            className="bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl p-6 text-left text-white shadow-md hover:shadow-lg transition-all"
          >
            <Briefcase className="w-10 h-10 mb-3 opacity-90" />
            <h4 className="font-bold text-lg">Summer Employment</h4>
            <p className="text-amber-100 text-sm mt-1">Find summer jobs, internships, and programs</p>
          </button>
        </div>
      </div>

      {/* FAQ */}
      <FAQAccordion title="Common Questions" faqs={SECOND_CHANCE_FAQS} />

      {/* Financial Support */}
      <section className="bg-brand-50 border border-brand-200 rounded-2xl p-6 mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-brand-100 rounded-xl flex items-center justify-center">
            <DollarSign className="w-6 h-6 text-brand-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-brand-800">Financial Help Available</h2>
            <p className="text-sm text-brand-600">Don't let money stop you</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4">
            <h3 className="font-bold text-gray-800 mb-2">Students' Loan Bureau</h3>
            <p className="text-sm text-gray-600 mb-2">Government loans for tertiary education. Low interest, pay back after you finish.</p>
            <a href="https://www.slb.gov.jm" target="_blank" rel="noopener noreferrer" className="text-brand-600 text-sm font-medium flex items-center gap-1 hover:underline">
              Visit SLB <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <div className="bg-white rounded-xl p-4">
            <h3 className="font-bold text-gray-800 mb-2">PATH Tertiary Grant</h3>
            <p className="text-sm text-gray-600 mb-2">If you're on PATH, you may qualify for tertiary education grants.</p>
            <a href="https://www.mlss.gov.jm/path" target="_blank" rel="noopener noreferrer" className="text-brand-600 text-sm font-medium flex items-center gap-1 hover:underline">
              Learn More <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <div className="bg-white rounded-xl p-4">
            <h3 className="font-bold text-gray-800 mb-2">HEART is FREE</h3>
            <p className="text-sm text-gray-600 mb-2">Most HEART training is free and many programmes include monthly stipends.</p>
            <a href="https://www.heart-nsta.org" target="_blank" rel="noopener noreferrer" className="text-brand-600 text-sm font-medium flex items-center gap-1 hover:underline">
              Explore HEART <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </section>

      {/* Related Resources */}
      <ResourceLinkGrid title="Related Resources" items={relatedResources} setPage={setPage} />
    </div>
  );
};

export default SecondChanceEducationHub;
